import * as Sentry from '@sentry/vue';
import App from '@/App.vue';
import colors from '@/assets/js/colors';
import router from '@/router';
import routes from '@/router/routes';
import useBetaPhaseManager from 'shared/services/beta-phase-manager';
import useBreakpointManager from 'shared/services/breakpoint-manager';
import useBrowserSvc from '@/services/browser';
import useEventBus from 'shared/services/event-bus';
import useSubscriptionManager from 'shared/services/subscription-manager';
import {
  BETA_PHASE_MANAGER,
  BREAKPOINT_MANAGER,
  BROWSER_SERVICE,
  EVENT_BUS,
  FIREBASE_APP,
  SUBSCRIPTION_MANAGER,
} from 'shared/constants';
import { createApp } from 'vue';
import { fontSizes } from '@/assets/js/font-sizes';
import { initDB } from '@/db/db';
import { makeBreakpoint } from 'shared/services/breakpoint';

const firebaseApp = initDB();

const app = createApp(App);
app.use(router);
app.provide(FIREBASE_APP, firebaseApp);

const eventBus = useEventBus();
app.provide(EVENT_BUS, eventBus);

const browserSvc = useBrowserSvc(firebaseApp.db, import.meta.env.VITE_EXTENSION_ID);
app.provide(BROWSER_SERVICE, browserSvc.getBrowser());

const subscriptionManager = useSubscriptionManager();
app.provide(SUBSCRIPTION_MANAGER, subscriptionManager);

const betaPhaseManager = useBetaPhaseManager(firebaseApp.db, subscriptionManager);
app.provide(BETA_PHASE_MANAGER, betaPhaseManager);

const breakpointManager = useBreakpointManager();
app.provide(BREAKPOINT_MANAGER, breakpointManager);

if (import.meta.env.PROD) {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN_EVILMORTY,
    integrations: [
      new Sentry.BrowserTracing({
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: ['localhost', 'notix.so', 'app.notix.so'],
        routingInstrumentation: Sentry.vueRouterInstrumentation(router),
      }),
      new Sentry.Replay(),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0,
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

app.mixin({
  computed: {
    $firebaseApp: () => firebaseApp.app,
    $db: () => firebaseApp.db,
    $functions: () => firebaseApp.functions,
    $storage: () => firebaseApp.storage,
    $auth: () => firebaseApp.auth,
    $analytics: () => firebaseApp.analytics,
    $colors: () => colors,
    $fontSizes: () => fontSizes,
    $routes: () => routes,
    $breakpoint: () => makeBreakpoint(),
    $browser: () => browserSvc.getBrowser(),
    $eventBus: () => eventBus,
    $subscriptionManager: () => subscriptionManager,
  },
});

router.isReady().then(() => app.mount('#app'));
